var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/api/useDebounce.ts
import { useEffect, useState } from "react";
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// src/contexts/auth.tsx
import React, { createContext, useContext, useMemo } from "react";

// src/auth/useAuth.ts
import { useCallback, useEffect as useEffect2, useState as useState2 } from "react";

// src/lib/api.ts
var ACCESS_TOKEN_KEY = "access_token";
var REFRESH_TOKEN_KEY = "refresh_token";
var TOKEN_EXPIRY_KEY = "token_expiry";
var USER_DATA_KEY = "user_data";
var AUTH_STATE_KEY = "auth_state";
var baseApiUrl = "";
var storageInterface = null;
var initializeApiClient = (apiUrl, storage) => {
  console.log("apiUrl FROM PARENT", apiUrl);
  baseApiUrl = apiUrl;
  storageInterface = storage;
};
var isRefreshing = false;
var refreshQueue = [];
var lastRefreshAttempt = 0;
var REFRESH_THROTTLE_MS = 5e3;
var processQueue = (token) => {
  refreshQueue.forEach((resolve) => resolve(token));
  refreshQueue = [];
};
var getAccessToken = async () => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    return await storageInterface.getItem(ACCESS_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting access token:", e);
    return null;
  }
};
var getRefreshToken = async () => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    return await storageInterface.getItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting refresh token:", e);
    return null;
  }
};
var getTokenExpiry = async () => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    const expiry = await storageInterface.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  } catch (e) {
    console.error("Error getting token expiry:", e);
    return null;
  }
};
var isTokenExpired = async () => {
  const expiry = await getTokenExpiry();
  if (!expiry)
    return true;
  return Date.now() > expiry - 3e4;
};
var apiClient = {
  get: async (endpoint, options) => {
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}${endpoint}`;
    const response = await fetch(fullUrl, __spreadProps(__spreadValues({}, options), {
      method: "GET",
      headers: __spreadValues(__spreadValues({
        "Content-Type": "application/json"
      }, token ? { Authorization: `Bearer ${token}` } : {}), (options == null ? void 0 : options.headers) || {})
    }));
    if (response.status === 401) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.get(endpoint, options);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  post: async (endpoint, data, options) => {
    const isAuthEndpoint = endpoint.includes("/auth/login") || endpoint.includes("/auth/signup") || endpoint.includes("/auth/forgot-password");
    if (!isAuthEndpoint && await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}${endpoint}`;
    const response = await fetch(fullUrl, __spreadProps(__spreadValues({}, options), {
      method: "POST",
      headers: __spreadValues(__spreadValues({
        "Content-Type": "application/json"
      }, token && !isAuthEndpoint ? { Authorization: `Bearer ${token}` } : {}), (options == null ? void 0 : options.headers) || {}),
      body: JSON.stringify(data)
    }));
    if (response.status === 401 && !isAuthEndpoint) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.post(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  put: async (endpoint, data, options) => {
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}${endpoint}`;
    const response = await fetch(fullUrl, __spreadProps(__spreadValues({}, options), {
      method: "PUT",
      headers: __spreadValues(__spreadValues({
        "Content-Type": "application/json"
      }, token ? { Authorization: `Bearer ${token}` } : {}), (options == null ? void 0 : options.headers) || {}),
      body: JSON.stringify(data)
    }));
    if (response.status === 401) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.put(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  patch: async (endpoint, data, options) => {
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}${endpoint}`;
    const response = await fetch(fullUrl, __spreadProps(__spreadValues({}, options), {
      method: "PATCH",
      headers: __spreadValues(__spreadValues({
        "Content-Type": "application/json"
      }, token ? { Authorization: `Bearer ${token}` } : {}), (options == null ? void 0 : options.headers) || {}),
      body: JSON.stringify(data)
    }));
    if (response.status === 401) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.patch(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  delete: async (endpoint, options) => {
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}${endpoint}`;
    const response = await fetch(fullUrl, __spreadProps(__spreadValues({}, options), {
      method: "DELETE",
      headers: __spreadValues(__spreadValues({
        "Content-Type": "application/json"
      }, token ? { Authorization: `Bearer ${token}` } : {}), (options == null ? void 0 : options.headers) || {})
    }));
    if (response.status === 401) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.delete(endpoint, options);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  changePassword: async (oldPassword, newPassword) => {
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }
    const token = await getAccessToken();
    const fullUrl = `${baseApiUrl}/auth/change-password`;
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: __spreadValues({
        "Content-Type": "application/json"
      }, token ? { Authorization: `Bearer ${token}` } : {}),
      body: JSON.stringify({ oldPassword, newPassword })
    });
    if (response.status === 401) {
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        return apiClient.changePassword(oldPassword, newPassword);
      } else {
        throw new Error("Authentication failed");
      }
    }
    const responseData = await response.json();
    if (!response.ok) {
      const error = responseData;
      throw error;
    }
    return responseData;
  },
  // Helper method to refresh token if needed with throttling
  refreshTokenIfNeeded: async () => {
    const now = Date.now();
    if (now - lastRefreshAttempt < REFRESH_THROTTLE_MS) {
      console.log("Token refresh throttled, waiting for previous attempt");
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push(() => resolve(true));
        });
      }
      return false;
    }
    lastRefreshAttempt = now;
    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push(() => resolve(true));
      });
    }
    if (await isTokenExpired()) {
      isRefreshing = true;
      try {
        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
          console.error("No refresh token available");
          isRefreshing = false;
          return false;
        }
        const fullUrl = `${baseApiUrl}/auth/refresh`;
        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ refreshToken })
        });
        if (!response.ok) {
          throw new Error(`Failed to refresh token: ${response.status}`);
        }
        const data = await response.json();
        if (!storageInterface) {
          console.error("Storage interface not initialized");
          isRefreshing = false;
          return false;
        }
        const expiryTime = Date.now() + data.expiresIn * 1e3;
        await storageInterface.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        await storageInterface.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        await storageInterface.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
        isRefreshing = false;
        processQueue(data.accessToken);
        return true;
      } catch (error) {
        console.error("Error refreshing token:", error);
        isRefreshing = false;
        refreshQueue.forEach((resolve) => resolve(""));
        refreshQueue = [];
        return false;
      }
    }
    return true;
  }
};

// src/auth/useAuth.ts
function useAuth(config) {
  const { storage, apiUrl } = config;
  useEffect2(() => {
    if (apiUrl) {
      initializeApiClient(apiUrl, storage);
    }
  }, [apiUrl, storage]);
  const [isLoaded, setIsLoaded] = useState2(false);
  const [isSignedIn, setIsSignedIn] = useState2(false);
  const [userId, setUserId] = useState2(null);
  const [user, setUser] = useState2(null);
  const [permissions, setPermissions] = useState2([]);
  const [error, setError] = useState2(null);
  const getRefreshToken2 = useCallback(async () => {
    return await storage.getItem(REFRESH_TOKEN_KEY);
  }, [storage]);
  const getTokenExpiry2 = useCallback(async () => {
    const expiry = await storage.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }, [storage]);
  const getUserData = useCallback(async () => {
    const userData = await storage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  }, [storage]);
  const isAuthenticated = useCallback(async () => {
    const authState = await storage.getItem(AUTH_STATE_KEY);
    return authState === "true";
  }, [storage]);
  const isTokenExpired2 = useCallback(async () => {
    const expiry = await getTokenExpiry2();
    if (!expiry)
      return true;
    return Date.now() > expiry - 3e4;
  }, [getTokenExpiry2]);
  const setAuthData = useCallback(
    async (userData, tokens) => {
      const expiryTime = Date.now() + tokens.expiresIn * 1e3;
      await storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      await storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      await storage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      await storage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      await storage.setItem(AUTH_STATE_KEY, "true");
    },
    [storage]
  );
  const removeAuthData = useCallback(async () => {
    await storage.removeItem(ACCESS_TOKEN_KEY);
    await storage.removeItem(REFRESH_TOKEN_KEY);
    await storage.removeItem(TOKEN_EXPIRY_KEY);
    await storage.removeItem(USER_DATA_KEY);
    await storage.removeItem(AUTH_STATE_KEY);
  }, [storage]);
  const refreshToken = useCallback(async () => {
    try {
      const currentRefreshToken = await getRefreshToken2();
      if (!currentRefreshToken) {
        console.error("No refresh token available");
        return false;
      }
      const data = await apiClient.post("/auth/refresh", {
        refreshToken: currentRefreshToken
      });
      const expiryTime = Date.now() + data.expiresIn * 1e3;
      await storage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      await storage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      await storage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      await storage.setItem(AUTH_STATE_KEY, "true");
      console.log("Token refreshed successfully");
      return true;
    } catch (error2) {
      console.error("Error refreshing token:", error2);
      return false;
    }
  }, [apiClient, getRefreshToken2, storage]);
  const signIn = useCallback(
    async (phoneNumber, password) => {
      try {
        setError(null);
        const loginData = {
          phoneNumber,
          password
        };
        const response = await apiClient.post(
          "/auth/login",
          loginData
        );
        await setAuthData(response.user, {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          expiresIn: response.tokens.expiresIn
        });
        setUser(response.user);
        setUserId(response.user.id);
        setIsSignedIn(true);
        console.log("User signed in successfully");
        await fetchPermissions();
      } catch (err) {
        console.error("Error signing in:", err);
        setError(err.message || "Failed to sign in");
        throw err;
      }
    },
    [apiClient, setAuthData]
  );
  const initiateSignup = useCallback(
    async (signupData) => {
      try {
        setError(null);
        const response = await apiClient.post(
          "/auth/signup/initiate",
          signupData
        );
        return response;
      } catch (err) {
        console.error("Error initiating signup:", err);
        setError(err.message || "Failed to initiate signup");
        throw err;
      }
    },
    [apiClient]
  );
  const verifySignupOtp = useCallback(
    async (verifyData) => {
      try {
        setError(null);
        const response = await apiClient.post(
          "/auth/signup/verify",
          verifyData
        );
        await setAuthData(response.user, {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          expiresIn: response.tokens.expiresIn
        });
        setUser(response.user);
        setUserId(response.user.id);
        setIsSignedIn(true);
        console.log("User signed up successfully");
        await fetchPermissions();
      } catch (err) {
        console.error("Error verifying signup:", err);
        setError(err.message || "Failed to verify OTP");
        throw err;
      }
    },
    [apiClient, setAuthData]
  );
  const signUp = useCallback(
    async (phoneNumber, password, firstName, lastName, email) => {
      try {
        setError(null);
        const signupData = {
          phoneNumber,
          password,
          firstName,
          lastName,
          email
        };
        return await initiateSignup(signupData);
      } catch (err) {
        console.error("Error in signup process:", err);
        setError(err.message || "Failed to sign up");
        throw err;
      }
    },
    [initiateSignup]
  );
  const forgotPassword = useCallback(
    async (phoneNumber) => {
      try {
        setError(null);
        const forgotPasswordData = { phoneNumber };
        const response = await apiClient.post(
          "/auth/forgot-password",
          forgotPasswordData
        );
        return response;
      } catch (err) {
        console.error("Error in forgot password process:", err);
        setError(err.message || "Failed to send password reset OTP");
        throw err;
      }
    },
    [apiClient]
  );
  const resetPassword = useCallback(
    async (resetData) => {
      var _a, _b;
      try {
        setError(null);
        await apiClient.post(
          "/auth/reset-password",
          resetData
        );
        console.log("Password reset successfully");
      } catch (err) {
        console.error("Error resetting password:", err);
        let errorMessage = "Failed to reset password";
        if ((_b = (_a = err.response) == null ? void 0 : _a.data) == null ? void 0 : _b.message) {
          errorMessage = Array.isArray(err.response.data.message) ? err.response.data.message.join(", ") : err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        throw err;
      }
    },
    [apiClient]
  );
  const signOut = useCallback(async () => {
    try {
      const refreshToken2 = await getRefreshToken2();
      if (refreshToken2) {
        try {
          await apiClient.post("/auth/logout", { refreshToken: refreshToken2 });
        } catch (logoutErr) {
          console.error("Error logging out from server:", logoutErr);
        }
      }
      await removeAuthData();
      setIsSignedIn(false);
      setUser(null);
      setUserId(null);
      console.log("User signed out successfully");
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Failed to sign out");
      try {
        await removeAuthData();
        setIsSignedIn(false);
        setUser(null);
        setUserId(null);
      } catch (clearErr) {
        console.error("Error clearing auth data:", clearErr);
      }
    }
  }, [apiClient, getRefreshToken2, removeAuthData]);
  const changePassword = useCallback(
    async (oldPassword, newPassword) => {
      try {
        setError(null);
        const response = await apiClient.changePassword(
          oldPassword,
          newPassword
        );
        return response;
      } catch (err) {
        console.error("Error changing password:", err);
        setError(err.message || "Failed to change password");
        throw err;
      }
    },
    [apiClient]
  );
  const fetchPermissions = useCallback(async () => {
    try {
      console.log("Fetching user permissions...");
      const response = await apiClient.get(
        "/auth/permissions"
      );
      const userPermissions = response.permissions || [];
      setPermissions(userPermissions);
      console.log(`Fetched ${userPermissions.length} permissions`);
      return userPermissions;
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setPermissions([]);
      return [];
    }
  }, [apiClient]);
  const refreshUserData = useCallback(async () => {
    try {
      setError(null);
      const response = await apiClient.get("/auth/me");
      setUser(response);
      await storage.setItem(USER_DATA_KEY, JSON.stringify(response));
      console.log("User data refreshed successfully");
      await fetchPermissions();
    } catch (err) {
      console.error("Error refreshing user data:", err);
      setError(err.message || "Failed to refresh user data");
      throw err;
    }
  }, [apiClient, storage, fetchPermissions]);
  useEffect2(() => {
    const checkAuth = async () => {
      try {
        const isUserAuthenticated = await isAuthenticated();
        console.log("Is user authenticated:", isUserAuthenticated);
        if (isUserAuthenticated) {
          const storedUserData = await getUserData();
          if (storedUserData) {
            console.log("Found stored user data, restoring session");
            setUser(storedUserData);
            setUserId(storedUserData.id);
            setIsSignedIn(true);
          } else {
            console.warn(
              "No stored user data found despite authenticated state"
            );
          }
          const hasExpired = await isTokenExpired2();
          if (hasExpired) {
            console.log("Token has expired, attempting refresh");
            const refreshed = await refreshToken();
            if (!refreshed) {
              console.log(
                "Token refresh failed, trying to fetch user profile as fallback"
              );
            } else {
              console.log("Token refreshed successfully");
            }
          }
          try {
            console.log("Fetching latest user profile");
            const userData = await apiClient.get("/auth/me");
            console.log("User profile fetched successfully");
            await storage.setItem(USER_DATA_KEY, JSON.stringify(userData));
            await storage.setItem(AUTH_STATE_KEY, "true");
            setUser(userData);
            setUserId(userData.id);
            setIsSignedIn(true);
            await fetchPermissions();
          } catch (profileError) {
            console.error("Error fetching user profile:", profileError);
            if (profileError.status === 401) {
              console.error("Auth error fetching user profile, signing out");
              await removeAuthData();
              setIsSignedIn(false);
              setUser(null);
              setUserId(null);
              setPermissions([]);
            } else {
              console.warn("Network error fetching profile, using cached data");
              await fetchPermissions();
            }
          }
        } else {
          console.log("No authentication data found, user is not signed in");
          setIsSignedIn(false);
          setUser(null);
          setUserId(null);
          setPermissions([]);
        }
      } catch (error2) {
        console.error("Error in auth check:", error2);
        setError(
          error2 instanceof Error ? error2 == null ? void 0 : error2.message : "Authentication error"
        );
        setUser(null);
        setUserId(null);
        setIsSignedIn(false);
        setPermissions([]);
      } finally {
        setIsLoaded(true);
      }
    };
    checkAuth();
  }, [
    apiClient,
    getUserData,
    isAuthenticated,
    isTokenExpired2,
    refreshToken,
    removeAuthData,
    storage,
    fetchPermissions
  ]);
  return {
    isLoaded,
    isSignedIn,
    userId,
    user,
    permissions,
    signIn,
    signUp,
    initiateSignup,
    verifySignupOtp,
    forgotPassword,
    resetPassword,
    changePassword,
    refreshUserData,
    fetchPermissions,
    signOut,
    error,
    setError
  };
}

// src/contexts/auth.tsx
var AuthContext = createContext({
  isLoaded: false,
  isSignedIn: false,
  userId: null,
  user: null,
  permissions: [],
  signIn: async () => {
  },
  signUp: async () => ({}),
  initiateSignup: async () => ({ success: false, message: "Not implemented" }),
  verifySignupOtp: async () => {
  },
  forgotPassword: async () => ({ success: false, message: "Not implemented" }),
  resetPassword: async () => {
  },
  changePassword: async () => ({ success: false, message: "Not implemented" }),
  refreshUserData: async () => {
  },
  fetchPermissions: async () => [],
  signOut: async () => {
  },
  error: null,
  setError: () => {
  }
});
var AuthProvider = ({ children, config }) => {
  const auth = useAuth(config);
  const value = useMemo(
    () => __spreadValues({}, auth),
    [
      auth.isLoaded,
      auth.isSignedIn,
      auth.userId,
      auth.user,
      auth.permissions,
      auth.error
    ]
  );
  return React.createElement(AuthContext.Provider, { value }, children);
};
var useAuthContext = () => useContext(AuthContext);

// src/contexts/permission.tsx
import React2, { createContext as createContext2, useContext as useContext2, useMemo as useMemo2 } from "react";
var PermissionContext = createContext2(void 0);
var PermissionProvider = ({
  children
}) => {
  const {
    isSignedIn,
    permissions: authPermissions,
    fetchPermissions
  } = useAuthContext();
  const refreshPermissions = async () => {
    if (isSignedIn) {
      await fetchPermissions();
    }
  };
  const hasPermission = (permission) => {
    if (!isSignedIn)
      return false;
    return authPermissions.includes(permission);
  };
  const hasAllPermissions = (permissions) => {
    if (!isSignedIn)
      return false;
    return permissions.every((permission) => hasPermission(permission));
  };
  const hasAnyPermission = (permissions) => {
    if (!isSignedIn)
      return false;
    return permissions.some((permission) => hasPermission(permission));
  };
  const value = useMemo2(
    () => ({
      userPermissions: authPermissions,
      // Pass through permissions from AuthContext
      hasPermission,
      hasAllPermissions,
      hasAnyPermission,
      refreshPermissions
    }),
    [authPermissions, isSignedIn]
  );
  return React2.createElement(PermissionContext.Provider, { value }, children);
};
var usePermissions = () => {
  const context = useContext2(PermissionContext);
  if (context === void 0) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};

// src/forms/usePaymentPlanForm.ts
import { z } from "zod";

// src/forms/useZodForm.ts
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
function useZodForm(schema, options) {
  return useForm(__spreadValues({
    resolver: zodResolver(schema)
  }, options));
}

// src/forms/usePaymentPlanForm.ts
import {
  PaymentType
} from "@niutraan/shared-dto";
var renewalOptionSchema = z.object({
  months: z.coerce.number().int().min(1, "Months is required"),
  price: z.coerce.number().min(0.01, "Price is required")
});
var paymentPlanSchema = z.object({
  name: z.string().min(1, "Payment plan name is required"),
  description: z.string().optional(),
  paymentType: z.nativeEnum(PaymentType),
  totalAmount: z.coerce.number().min(0.01, "Total amount is required"),
  minInitialAmount: z.coerce.number().optional(),
  // optional coercion
  minInstallmentPercentage: z.coerce.number().max(100, "Maximum 100% allowed").optional(),
  paymentWindowDays: z.coerce.number().int().optional(),
  supportPeriodDays: z.coerce.number().int().optional(),
  isActive: z.boolean().default(true),
  renewalOptions: z.array(renewalOptionSchema).min(1, "At least one renewal option is required")
}).refine(
  (data) => {
    if (data.paymentType === PaymentType.PARTIAL_PAYMENT && data.minInitialAmount != null) {
      return data.minInitialAmount < data.totalAmount;
    }
    return true;
  },
  {
    message: "Minimum initial amount must be less than total amount",
    path: ["minInitialAmount"]
  }
);
function usePaymentPlanForm(paymentPlanId, {
  usePaymentPlanById,
  useCreatePaymentPlan,
  useUpdatePaymentPlan,
  onSuccess,
  onError
}) {
  const isNewPlan = paymentPlanId === "new";
  const form = useZodForm(paymentPlanSchema, {
    defaultValues: {
      name: "",
      description: "",
      paymentType: PaymentType.FULL_PAYMENT,
      totalAmount: 0,
      minInitialAmount: void 0,
      minInstallmentPercentage: void 0,
      paymentWindowDays: 30,
      supportPeriodDays: 90,
      isActive: true,
      renewalOptions: [
        { months: 1, price: 1e3 },
        { months: 3, price: 2500 },
        { months: 6, price: 4e3 }
      ]
    }
  });
  const {
    data: paymentPlan,
    isLoading,
    error
  } = usePaymentPlanById(isNewPlan ? "" : paymentPlanId);
  const createMutation = useCreatePaymentPlan();
  const updateMutation = useUpdatePaymentPlan();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewPlan) {
        const result = await createMutation.mutateAsync(data);
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: (paymentPlan == null ? void 0 : paymentPlan.id) || "",
          updateData: data
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    paymentPlan,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewPlan
  };
}

// src/forms/useCreateCourseForm.ts
import {
  CourseType
} from "@niutraan/shared-dto";
import { z as z2 } from "zod";
var courseFormSchema = z2.object({
  title: z2.string().min(1, "Title is required"),
  description: z2.string().min(1, "Description is required"),
  imageUrl: z2.string().optional(),
  courseType: z2.enum(CourseType),
  isActive: z2.boolean(),
  content: z2.object({
    text: z2.string()
  }).optional(),
  childCourseIds: z2.array(z2.string()).optional(),
  paymentPlanId: z2.string().min(1, "Payment plan is required"),
  updatedById: z2.string().optional(),
  createdById: z2.string().optional()
});
function useCreateCourseForm(courseId, {
  useCourseById,
  useCreateCourse,
  useUpdateCourse,
  onSuccess,
  onError
}) {
  const isNewCourse = courseId === "new";
  const form = useZodForm(courseFormSchema, {
    defaultValues: {
      title: "",
      description: "",
      imageUrl: "",
      courseType: CourseType.OFFLINE,
      isActive: true,
      content: { text: "" },
      childCourseIds: [],
      updatedById: "",
      createdById: "",
      paymentPlanId: ""
    }
  });
  const {
    data: courseData,
    isLoading,
    error
  } = useCourseById(isNewCourse ? "" : courseId);
  const createMutation = useCreateCourse();
  const updateMutation = useUpdateCourse();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewCourse) {
        const result = await createMutation.mutateAsync(__spreadProps(__spreadValues({}, data), {
          paymentPlanIds: [data.paymentPlanId]
        }));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: courseId || "",
          data
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    courseData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewCourse
  };
}

// src/forms/useOnlineCourseForm.ts
import { z as z3 } from "zod";
var onlineCourseFormSchema = z3.object({
  courseDurationDays: z3.number().int().min(1, "Duration must be at least 1 day"),
  dailyUnlockLimit: z3.number().int().min(1, "Daily unlock limit must be at least 1")
});
function useOnlineCourseForm(courseId, {
  useOnlineCourseByCourseId,
  useCreateOnlineCourse,
  useUpdateOnlineCourse,
  onSuccess,
  onError
}) {
  const form = useZodForm(onlineCourseFormSchema, {
    defaultValues: {
      courseDurationDays: 30,
      dailyUnlockLimit: 2
    }
  });
  const { data: onlineCourseData } = useOnlineCourseByCourseId(courseId);
  const createOnlineCourseMutation = useCreateOnlineCourse();
  const updateOnlineCourseMutation = useUpdateOnlineCourse();
  const isNewCourse = !onlineCourseData;
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (!onlineCourseData) {
        const result = await createOnlineCourseMutation.mutateAsync({
          courseId,
          data: {
            courseId,
            language: "English",
            isPublished: false,
            totalRatings: 0,
            totalEnrollments: 0,
            courseDurationDays: Number(data.courseDurationDays),
            dailyUnlockLimit: Number(data.dailyUnlockLimit)
          }
        });
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        console.log(courseId);
        const result = await updateOnlineCourseMutation.mutateAsync({
          onlineCourseId: (onlineCourseData == null ? void 0 : onlineCourseData.id) || "",
          data: {
            courseDurationDays: Number(data.courseDurationDays),
            dailyUnlockLimit: Number(data.dailyUnlockLimit)
          }
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error) {
      onError == null ? void 0 : onError(error);
    }
  });
  return {
    form,
    onlineCourseData,
    isSubmitting: createOnlineCourseMutation.isPending || updateOnlineCourseMutation.isPending,
    handleSubmit,
    isNewCourse
  };
}

// src/forms/useInstitutionLocationForm.ts
import { z as z4 } from "zod";
var institutionLocationFormSchema = z4.object({
  name: z4.string().min(1, "Location name is required"),
  address: z4.string().min(1, "Address is required"),
  city: z4.string().min(1, "City is required"),
  state: z4.string().optional(),
  country: z4.string().min(1, "Country is required"),
  postalCode: z4.string().optional(),
  googleMapsLink: z4.string().optional(),
  isActive: z4.boolean()
});
function useCreateInstitutionLocationForm(id, {
  useInstitutionLocationById,
  useCreateInstitutionLocation,
  useUpdateInstitutionLocation,
  onSuccess,
  onError
}) {
  const isNewLocation = id === "new";
  const form = useZodForm(institutionLocationFormSchema, {
    defaultValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      googleMapsLink: "",
      isActive: true
    }
  });
  const {
    data: locationData,
    isLoading,
    error
  } = useInstitutionLocationById(isNewLocation ? "" : id != null ? id : "");
  const createMutation = useCreateInstitutionLocation();
  const updateMutation = useUpdateInstitutionLocation();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewLocation) {
        const result = await createMutation.mutateAsync(__spreadValues({}, data));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: id || "",
          data
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    locationData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewLocation
  };
}

// src/forms/useInstitutionStaffForm.tsx
import { z as z5 } from "zod";
var institutionStaffFormSchema = z5.object({
  userId: z5.string().min(1, "Location name is required"),
  institutionLocationId: z5.string().min(1, "Location name is required"),
  roleId: z5.string().min(1, "Location name is required"),
  isActive: z5.boolean(),
  createdById: z5.string().optional()
});
function useCreateInstitutionStaffForm(id, {
  useInstitutionStaffById,
  useCreateInstitutionStaff,
  useUpdateInstitutionStaff,
  onSuccess,
  onError
}) {
  const isNewStaff = id === "new";
  const form = useZodForm(institutionStaffFormSchema, {
    defaultValues: {
      userId: "",
      roleId: "",
      createdById: "",
      institutionLocationId: "",
      isActive: true
    }
  });
  const {
    data: staffData,
    isLoading,
    error
  } = useInstitutionStaffById(isNewStaff ? "" : id != null ? id : "");
  const createMutation = useCreateInstitutionStaff();
  const updateMutation = useUpdateInstitutionStaff();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewStaff) {
        const result = await createMutation.mutateAsync(__spreadValues({}, data));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: id || "",
          data: __spreadValues({ id }, data)
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    staffData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewStaff
  };
}

// src/forms/useCreateHubFeature.ts
import { z as z6 } from "zod";
var hubFeatureFormSchema = z6.object({
  name: z6.string().min(1, "Name is required"),
  description: z6.string().min(1, "Description is required"),
  updatedById: z6.string().optional(),
  createdById: z6.string().optional()
});
function useCreateHubForm(hubId, {
  useHubFeatureById,
  useCreateHubFeature,
  useUpdateHubFeature,
  onSuccess,
  onError
}) {
  const isNewHub = hubId === "new";
  const form = useZodForm(hubFeatureFormSchema, {
    defaultValues: {
      name: "",
      description: "",
      updatedById: "",
      createdById: ""
    }
  });
  const {
    data: hubfeatureData,
    isLoading,
    error
  } = useHubFeatureById(isNewHub ? "" : hubId);
  const createMutation = useCreateHubFeature();
  const updateMutation = useUpdateHubFeature();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewHub) {
        const result = await createMutation.mutateAsync(__spreadValues({}, data));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: hubId || "",
          data
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    hubfeatureData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewHub
  };
}

// src/forms/useCreateHubTierForm.ts
import { z as z7 } from "zod";
var hubTierFormSchema = z7.object({
  name: z7.string().min(1, "Name is required"),
  description: z7.string().min(1, "Description is required"),
  sequence: z7.string().min(1, "Sequence is required"),
  updatedById: z7.string().optional(),
  createdById: z7.string().optional(),
  featureIds: z7.array(z7.string()).optional()
});
function useCreateHubTierForm(hubId, {
  useHubTierById,
  useCreateHubTier,
  useUpdateHubTier,
  onSuccess,
  onError
}) {
  const isNewHub = hubId === "new";
  const form = useZodForm(hubTierFormSchema, {
    defaultValues: {
      name: "",
      description: "",
      sequence: "0",
      createdById: "",
      updatedById: "",
      featureIds: []
    }
  });
  const {
    data: hubTierData,
    isLoading,
    error
  } = useHubTierById(isNewHub ? "" : hubId);
  const createMutation = useCreateHubTier();
  const updateMutation = useUpdateHubTier();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewHub) {
        const result = await createMutation.mutateAsync(__spreadProps(__spreadValues({}, data), {
          sequence: parseInt(data.sequence) || 0,
          featureIds: data.featureIds
        }));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: hubId || "",
          data: {
            name: data.name,
            description: data.description || void 0,
            sequence: parseInt(data.sequence) || 0
          }
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    hubTierData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewHub
  };
}

// src/forms/useCreateHubPlanForm .ts
import { z as z8 } from "zod";
var hubPlanFormSchema = z8.object({
  name: z8.string().min(1, "Name is required"),
  description: z8.string().min(1, "Description is required"),
  price: z8.coerce.number().min(1, "Price amount must be greater than 0"),
  totalHours: z8.coerce.number().min(1, "Total hours must be greater than 0"),
  bonusHours: z8.coerce.number().optional(),
  tierId: z8.string().optional(),
  validityMonths: z8.coerce.number().min(1, "Validity months must be greater than 0"),
  hourlyRate: z8.coerce.number().min(1, "Hourly  rate must be greater than 0"),
  isActive: z8.boolean().default(true),
  updatedById: z8.string().optional(),
  createdById: z8.string().optional()
});
function useCreateHubPlanForm(hubId, {
  useHubPlanById,
  useCreateHubPlan,
  useUpdateHubPlan,
  onSuccess,
  onError
}) {
  const isNewHub = hubId === "new";
  const form = useZodForm(hubPlanFormSchema, {
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      totalHours: 0,
      bonusHours: 0,
      tierId: "",
      validityMonths: 0,
      hourlyRate: 0,
      isActive: true,
      createdById: "",
      updatedById: ""
    }
  });
  const {
    data: hubPlanData,
    isLoading,
    error
  } = useHubPlanById(isNewHub ? "" : hubId);
  const createMutation = useCreateHubPlan();
  const updateMutation = useUpdateHubPlan();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewHub) {
        const result = await createMutation.mutateAsync(__spreadValues({}, data));
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: hubId || "",
          data: __spreadValues({}, data)
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    hubPlanData,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewHub
  };
}

// src/forms/useHubAdjustmentForm.tsx
import { z as z9 } from "zod";
var hubHourAdjustmentFormSchema = z9.object({
  hubEnrollmentId: z9.string().optional(),
  hours: z9.coerce.number().min(1, "Hours is required"),
  reason: z9.string().min(1, "Reason is required"),
  createdById: z9.string().optional(),
  userId: z9.string().optional(),
  editingId: z9.string().optional()
});
function useCreateHubHourAdjustmentForm(hubEnrollmentId, {
  useHubHourAdjustments,
  useCreateHubHourAdjustment,
  useUpdateHubHourAdjustment,
  onSuccess,
  onError
}) {
  const form = useZodForm(hubHourAdjustmentFormSchema, {
    defaultValues: {
      hubEnrollmentId: "",
      hours: 0,
      reason: "",
      createdById: "",
      userId: "",
      editingId: ""
    }
  });
  const {
    data: hourAdjustments = [],
    isLoading: isAdjustmentsLoading,
    error: adjustmentsError,
    refetch: refetchAdjustments,
    isRefetching: isAdjustmentsRefetching
  } = useHubHourAdjustments(hubEnrollmentId);
  const createMutation = useCreateHubHourAdjustment();
  const updateMutation = useUpdateHubHourAdjustment();
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (data.editingId == "new") {
        const result = await createMutation.mutateAsync({
          hubEnrollmentId,
          hours: data.hours,
          reason: data.reason,
          createdById: data.userId
        });
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const result = await updateMutation.mutateAsync({
          id: data.editingId,
          data: {
            hours: data.hours,
            reason: data.reason,
            updatedById: data.userId
          }
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error) {
      onError == null ? void 0 : onError(error);
    }
  });
  return {
    form,
    hourAdjustments,
    isAdjustmentsLoading,
    adjustmentsError,
    refetchAdjustments,
    isAdjustmentsRefetching,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit
  };
}

// src/forms/useOfferForm.ts
import { z as z10 } from "zod";
import {
  DiscountType,
  OfferType
} from "@niutraan/shared-dto";
import { useState as useState3, useEffect as useEffect3 } from "react";
var offerFormSchema = z10.object({
  name: z10.string().min(1, "Offer name is required"),
  description: z10.string().optional(),
  offerType: z10.nativeEnum(OfferType),
  discountType: z10.nativeEnum(DiscountType),
  discountValue: z10.number().positive("Discount value must be positive"),
  startDate: z10.string().min(1, "Start date is required"),
  endDate: z10.string().min(1, "End date is required"),
  isActive: z10.boolean(),
  isRenewalOffer: z10.boolean(),
  minimumEnrollments: z10.number().optional(),
  maxUsageCount: z10.number().optional(),
  daysSinceEnrollment: z10.number().optional(),
  daysBeforeExpiry: z10.number().optional(),
  autoApply: z10.boolean(),
  priority: z10.number().optional(),
  code: z10.string().optional(),
  offerCodeImageUrl: z10.string().url().optional().or(z10.literal("")),
  isPublic: z10.boolean(),
  targetUserGroups: z10.string().optional(),
  minPurchaseAmount: z10.number().optional(),
  isStackable: z10.boolean(),
  stackPriority: z10.number().optional(),
  isReferralOffer: z10.boolean(),
  referralCode: z10.string().optional(),
  referrerUserId: z10.string().optional(),
  referrerReward: z10.number().optional(),
  perUserLimit: z10.number().optional(),
  isThirdPartyCoupon: z10.boolean(),
  // Offer scope
  isAvailableToAllCourses: z10.boolean(),
  availableCourseIds: z10.array(z10.string()).optional()
}).refine(
  (data) => {
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    return startDate < endDate;
  },
  {
    message: "End date must be after start date",
    path: ["endDate"]
  }
).refine(
  (data) => {
    if (data.discountType === DiscountType.PERCENTAGE) {
      return data.discountValue <= 100;
    }
    return true;
  },
  {
    message: "Percentage discount cannot exceed 100%",
    path: ["discountValue"]
  }
);
function useOfferForm(offerId, {
  useOfferById,
  useCreateOffer,
  useUpdateOffer,
  buildCreateOfferDto,
  buildUpdateOfferDto,
  parseOfferDataForForm,
  parseThirdPartyCoupons,
  onSuccess,
  onError
}) {
  const isNewOffer = offerId === "new";
  const [thirdPartyCoupons, setThirdPartyCoupons] = useState3([]);
  const form = useZodForm(offerFormSchema, {
    defaultValues: {
      name: "",
      description: "",
      offerType: OfferType.DISCOUNT,
      discountType: DiscountType.PERCENTAGE,
      discountValue: 0,
      startDate: (/* @__PURE__ */ new Date()).toISOString().split("T")[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1e3).toISOString().split("T")[0],
      isActive: true,
      isRenewalOffer: false,
      autoApply: false,
      isPublic: true,
      isStackable: false,
      isReferralOffer: false,
      isThirdPartyCoupon: false,
      isAvailableToAllCourses: true,
      availableCourseIds: void 0
    }
  });
  const {
    data: offer,
    isLoading,
    error
  } = useOfferById(isNewOffer ? "" : offerId);
  const createMutation = useCreateOffer();
  const updateMutation = useUpdateOffer();
  useEffect3(() => {
    if (offer && !isNewOffer) {
      const formValues = parseOfferDataForForm(offer);
      form.reset(formValues);
      const coupons = parseThirdPartyCoupons(offer.thirdPartyCoupons);
      if (coupons.length > 0) {
        setThirdPartyCoupons(coupons);
      }
    }
  }, [offer, isNewOffer, form, parseOfferDataForForm, parseThirdPartyCoupons]);
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewOffer) {
        const createData = buildCreateOfferDto(data, thirdPartyCoupons);
        const result = await createMutation.mutateAsync(createData);
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const updateData = buildUpdateOfferDto(data, thirdPartyCoupons);
        const result = await updateMutation.mutateAsync({
          id: (offer == null ? void 0 : offer.id) || "",
          data: updateData
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    offer,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewOffer,
    thirdPartyCoupons,
    setThirdPartyCoupons
  };
}

// src/forms/useLiveEventForm.ts
import { z as z11 } from "zod";
import {
  LiveEventType,
  LiveEventStatus
} from "@niutraan/shared-dto";
import { useEffect as useEffect4 } from "react";
var liveEventFormSchema = z11.object({
  title: z11.string().min(1, "Event title is required"),
  description: z11.string().min(1, "Description is required"),
  eventType: z11.enum(LiveEventType),
  startDateTime: z11.string().min(1, "Start date/time is required"),
  endDateTime: z11.string().min(1, "End date/time is required"),
  registrationStartDate: z11.string().min(1, "Registration start date is required"),
  registrationEndDate: z11.string().min(1, "Registration end date is required"),
  location: z11.string().min(1, "Location is required"),
  address: z11.string().min(1, "Address is required"),
  city: z11.string().min(1, "City is required"),
  state: z11.string().optional(),
  country: z11.string().min(1, "Country is required"),
  postalCode: z11.string().optional(),
  latitude: z11.number().optional(),
  longitude: z11.number().optional(),
  googleMapsLink: z11.url().optional().or(z11.literal("")),
  institutionLocationId: z11.string().optional(),
  maxAttendees: z11.number().int().positive().optional(),
  paymentPlanId: z11.string().min(1, "Payment plan is required"),
  status: z11.enum(LiveEventStatus),
  isPublic: z11.boolean(),
  isFeatured: z11.boolean(),
  requirements: z11.string().optional(),
  additionalInfo: z11.string().optional(),
  imageUrls: z11.array(z11.url()).optional(),
  eventKits: z11.array(
    z11.object({
      eventKitId: z11.string(),
      quantity: z11.number().int().positive(),
      isRequired: z11.boolean().optional()
    })
  ).optional(),
  eventOffers: z11.array(
    z11.object({
      offerId: z11.string(),
      isAutoAssign: z11.boolean().optional()
    })
  ).optional()
}).refine(
  (data) => {
    const start = new Date(data.startDateTime);
    const end = new Date(data.endDateTime);
    return start < end;
  },
  {
    message: "End date/time must be after start date/time",
    path: ["endDateTime"]
  }
).refine(
  (data) => {
    const regStart = new Date(data.registrationStartDate);
    const regEnd = new Date(data.registrationEndDate);
    return regStart < regEnd;
  },
  {
    message: "Registration end date must be after registration start date",
    path: ["registrationEndDate"]
  }
);
function useLiveEventForm(eventId, {
  useLiveEventById,
  useCreateLiveEvent,
  useUpdateLiveEvent,
  onSuccess,
  onError
}) {
  const isNewEvent = eventId === "new";
  const form = useZodForm(liveEventFormSchema, {
    defaultValues: {
      title: "",
      description: "",
      eventType: LiveEventType.WORKSHOP,
      startDateTime: (/* @__PURE__ */ new Date()).toISOString().slice(0, 16),
      endDateTime: new Date(Date.now() + 2 * 60 * 60 * 1e3).toISOString().slice(0, 16),
      registrationStartDate: (/* @__PURE__ */ new Date()).toISOString().slice(0, 16),
      registrationEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1e3).toISOString().slice(0, 16),
      location: "",
      address: "",
      city: "",
      state: void 0,
      country: "",
      postalCode: void 0,
      latitude: void 0,
      longitude: void 0,
      googleMapsLink: void 0,
      institutionLocationId: void 0,
      maxAttendees: void 0,
      paymentPlanId: "",
      status: LiveEventStatus.UPCOMING,
      isPublic: true,
      isFeatured: false,
      requirements: void 0,
      additionalInfo: void 0,
      imageUrls: void 0,
      eventKits: [],
      eventOffers: []
    }
  });
  const {
    data: event,
    isLoading,
    error
  } = useLiveEventById(isNewEvent ? "" : eventId);
  const createMutation = useCreateLiveEvent();
  const updateMutation = useUpdateLiveEvent();
  useEffect4(() => {
    var _a, _b, _c, _d;
    if (event && !isNewEvent) {
      form.reset({
        title: event.title,
        description: event.description,
        eventType: event.eventType,
        startDateTime: new Date(event.startDateTime).toISOString().slice(0, 16),
        endDateTime: new Date(event.endDateTime).toISOString().slice(0, 16),
        registrationStartDate: new Date(event.registrationStartDate).toISOString().slice(0, 16),
        registrationEndDate: new Date(event.registrationEndDate).toISOString().slice(0, 16),
        location: event.location,
        address: event.address,
        city: event.city,
        state: event.state,
        country: event.country,
        postalCode: event.postalCode,
        latitude: event.latitude,
        longitude: event.longitude,
        googleMapsLink: event.googleMapsLink,
        institutionLocationId: (_a = event.institutionLocation) == null ? void 0 : _a.id,
        maxAttendees: event.maxAttendees,
        paymentPlanId: (_b = event.paymentPlan) == null ? void 0 : _b.id,
        status: event.status,
        isPublic: event.isPublic,
        isFeatured: event.isFeatured,
        requirements: event.requirements,
        additionalInfo: event.additionalInfo,
        imageUrls: event.imageUrls,
        eventKits: ((_c = event.eventKits) == null ? void 0 : _c.map((kit) => {
          var _a2;
          return {
            eventKitId: ((_a2 = kit.eventKit) == null ? void 0 : _a2.id) || "",
            quantity: kit.quantity || 1,
            isRequired: kit.isRequired
          };
        })) || [],
        eventOffers: ((_d = event.eventOffers) == null ? void 0 : _d.map((offer) => {
          var _a2;
          return {
            offerId: ((_a2 = offer.offer) == null ? void 0 : _a2.id) || "",
            isAutoAssign: offer.isAutoAssign
          };
        })) || []
      });
    }
  }, [event, isNewEvent, form]);
  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      if (isNewEvent) {
        const createData = __spreadProps(__spreadValues({}, data), {
          startDateTime: new Date(data.startDateTime).toISOString(),
          endDateTime: new Date(data.endDateTime).toISOString(),
          registrationStartDate: new Date(
            data.registrationStartDate
          ).toISOString(),
          registrationEndDate: new Date(data.registrationEndDate).toISOString()
        });
        const result = await createMutation.mutateAsync(createData);
        onSuccess == null ? void 0 : onSuccess(result);
      } else {
        const updateData = __spreadProps(__spreadValues({}, data), {
          startDateTime: new Date(data.startDateTime).toISOString(),
          endDateTime: new Date(data.endDateTime).toISOString(),
          registrationStartDate: new Date(
            data.registrationStartDate
          ).toISOString(),
          registrationEndDate: new Date(data.registrationEndDate).toISOString()
        });
        const result = await updateMutation.mutateAsync({
          id: (event == null ? void 0 : event.id) || "",
          data: updateData
        });
        onSuccess == null ? void 0 : onSuccess(result);
      }
    } catch (error2) {
      onError == null ? void 0 : onError(error2);
    }
  });
  return {
    form,
    event,
    isLoading,
    error,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    handleSubmit,
    isNewEvent
  };
}
export {
  AuthContext,
  AuthProvider,
  PermissionContext,
  PermissionProvider,
  courseFormSchema,
  hubFeatureFormSchema,
  hubHourAdjustmentFormSchema,
  hubPlanFormSchema,
  hubTierFormSchema,
  institutionLocationFormSchema,
  institutionStaffFormSchema,
  liveEventFormSchema,
  offerFormSchema,
  paymentPlanSchema,
  useAuthContext,
  useCreateCourseForm,
  useCreateHubForm,
  useCreateHubHourAdjustmentForm,
  useCreateHubPlanForm,
  useCreateHubTierForm,
  useCreateInstitutionLocationForm,
  useCreateInstitutionStaffForm,
  useDebounce,
  useLiveEventForm,
  useOfferForm,
  useOnlineCourseForm,
  usePaymentPlanForm,
  usePermissions,
  useZodForm
};
